import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { getCurrentUserProfile, updateCurrentUser } from "../services/api";
import { toast } from "react-toastify";

// API Key - à déplacer en variable d'environnement
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";

function Profile() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const pendingCheckout = searchParams.get("checkout") === "pending";

  // Références
  const autocompleteRef = useRef(null);
  const streetInputRef = useRef(null);
  const notificationDisplayed = useRef(false);
  const initialRenderDone = useRef(false);

  // États
  const [user, setUser] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    address: {
      street: "",
      address2: "",
      postalCode: "",
      city: "",
      region: "",
      country: "",
    },
    gender: "",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [placesLoaded, setPlacesLoaded] = useState(false);
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [passwords, setPasswords] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [incompleteFields, setIncompleteFields] = useState([]);

  // Charger les données utilisateur
  useEffect(() => {
    async function loadUser() {
      try {
        setLoading(true);
        const res = await getCurrentUserProfile();
        if (res.data.firstName && res.data.lastName) {
          localStorage.setItem(
            "userName",
            `${res.data.firstName} ${res.data.lastName}`
          );
        }
        setUser({
          ...res.data,
          address: res.data.address || {
            street: "",
            address2: "",
            postalCode: "",
            city: "",
            region: "",
            country: "",
          },
          gender: res.data.gender || "",
        });
      } catch (error) {
        console.error("Erreur de chargement du profil:", error);
        toast.error("Impossible de charger votre profil");
      } finally {
        setLoading(false);
      }
    }
    loadUser();
  }, []);

  // Vérifier les champs requis et mettre à jour les champs incomplets
  useEffect(() => {
    if (!loading && pendingCheckout) {
      const missing = [];
      if (!user.firstName) missing.push("firstName");
      if (!user.lastName) missing.push("lastName");
      if (!user.phone) missing.push("phone");
      if (!user.address.street) missing.push("address.street");
      if (!user.address.postalCode) missing.push("address.postalCode");
      if (!user.address.city) missing.push("address.city");
      if (!user.address.country) missing.push("address.country");

      setIncompleteFields(missing);

      // N'afficher la notification qu'une seule fois après le premier rendu complet
      // et seulement si les champs sont incomplets
      if (
        missing.length > 0 &&
        !notificationDisplayed.current &&
        initialRenderDone.current
      ) {
        toast.info(
          "Veuillez compléter les informations manquantes pour finaliser votre commande"
        );
        notificationDisplayed.current = true;
      }

      // Marquer que le rendu initial est terminé
      if (!initialRenderDone.current) {
        initialRenderDone.current = true;
      }
    }
  }, [loading, pendingCheckout, user]);

  // Charger l'API Google Maps et initialiser l'autocomplétion
  useEffect(() => {
    // Fonction pour initialiser l'autocomplétion après le chargement de l'API
    function initializeAutocomplete() {
      if (!streetInputRef.current) {
        console.warn("Référence à l'input d'adresse non disponible");
        return;
      }

      try {
        console.log("Initialisation de l'autocomplétion...");
        autocompleteRef.current = new window.google.maps.places.Autocomplete(
          streetInputRef.current,
          { types: ["address"] }
        );

        autocompleteRef.current.addListener("place_changed", () => {
          console.log("Événement place_changed déclenché");
          const place = autocompleteRef.current.getPlace();

          if (!place.address_components) {
            console.warn(
              "Pas de composants d'adresse dans le lieu sélectionné"
            );
            return;
          }

          let street = "",
            city = "",
            postalCode = "",
            region = "",
            country = "";

          place.address_components.forEach((component) => {
            const types = component.types;
            if (types.includes("street_number")) street = component.long_name;
            if (types.includes("route")) {
              street = street
                ? `${street} ${component.long_name}`
                : component.long_name;
            }
            if (types.includes("locality")) city = component.long_name;
            if (types.includes("postal_code")) postalCode = component.long_name;
            if (types.includes("administrative_area_level_1"))
              region = component.long_name;
            if (types.includes("country")) country = component.long_name;
          });

          console.log("Adresse extraite:", {
            street,
            city,
            postalCode,
            region,
            country,
          });

          // Mettre à jour les champs de l'adresse
          setUser((prev) => ({
            ...prev,
            address: {
              ...prev.address,
              street,
              city,
              postalCode,
              region,
              country,
            },
          }));

          // Supprimer les champs d'adresse des champs incomplets
          setIncompleteFields((prev) =>
            prev.filter(
              (field) =>
                ![
                  "address.street",
                  "address.postalCode",
                  "address.city",
                  "address.country",
                ].includes(field)
            )
          );

          toast.success("Adresse complétée automatiquement");
        });

        console.log("Autocomplétion initialisée avec succès");
      } catch (error) {
        console.error("Erreur d'initialisation de l'autocomplétion:", error);
        toast.error("Impossible d'initialiser l'autocomplétion d'adresse");
      }
    }

    // Si l'API est déjà chargée
    if (window.google?.maps?.places) {
      console.log("API Google Maps déjà chargée");
      setPlacesLoaded(true);
      initializeAutocomplete();
      return;
    }

    // Vérifier si la clé API est définie
    if (!GOOGLE_MAPS_API_KEY) {
      console.error("Clé API Google Maps non définie");
      return;
    }

    // Vérifier si un script existe déjà
    const existingScript = document.querySelector(
      'script[src*="maps.googleapis.com"]'
    );

    if (existingScript) {
      console.log("Script Google Maps déjà présent dans le DOM");
      return;
    }

    console.log("Chargement de l'API Google Maps...");

    // Définir le callback avant de créer le script
    window.initGoogleMaps = () => {
      console.log("API Google Maps chargée avec succès");
      setPlacesLoaded(true);
      // Petit délai pour s'assurer que tout est bien initialisé
      setTimeout(initializeAutocomplete, 100);
    };

    // Créer et ajouter le script
    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places&callback=initGoogleMaps`;
    script.async = true;
    script.onerror = () => {
      console.error("Erreur lors du chargement de l'API Google Maps");
      toast.error("Impossible de charger le service d'autocomplétion");
    };
    document.head.appendChild(script);

    // Nettoyage
    return () => {
      if (window.google && autocompleteRef.current) {
        window.google.maps.event.clearInstanceListeners(
          autocompleteRef.current
        );
      }
      delete window.initGoogleMaps;
    };
  }, []); // Exécuté une seule fois au montage du composant

  // Vérifier si tous les champs requis sont remplis pour le checkout
  const areAllRequiredFieldsFilled = useCallback(() => {
    return (
      user.firstName &&
      user.lastName &&
      user.phone &&
      user.address.street &&
      user.address.postalCode &&
      user.address.city &&
      user.address.country
    );
  }, [user]);

  // Gestion des changements de formulaire - Amélioré pour supprimer les cadres rouges immédiatement
  const handleChange = useCallback(
    (e) => {
      const { name, value, type, checked } = e.target;

      // Suppression des erreurs lors de la modification
      if (formErrors[name]) {
        setFormErrors((prev) => ({ ...prev, [name]: null }));
      }

      // Extraction du nom du champ pour les champs d'adresse
      const fieldName = name.startsWith("address.") ? name : "";
      const addressField = name.startsWith("address.")
        ? name.split(".")[1]
        : "";

      // Mise à jour des champs
      if (name.startsWith("address.")) {
        setUser((prev) => ({
          ...prev,
          address: { ...prev.address, [addressField]: value },
        }));

        // Supprimer le champ d'adresse des incomplets s'il n'est plus vide
        if (value && incompleteFields.includes(name)) {
          setIncompleteFields((prev) => prev.filter((field) => field !== name));
        }
      } else if (name.startsWith("password.")) {
        const passwordField = name.split(".")[1];
        setPasswords((prev) => ({ ...prev, [passwordField]: value }));
      } else {
        setUser((prev) => ({
          ...prev,
          [name]: type === "checkbox" ? checked : value,
        }));

        // Supprimer le champ des incomplets s'il n'est plus vide
        if (value && incompleteFields.includes(name)) {
          setIncompleteFields((prev) => prev.filter((field) => field !== name));
        }
      }
    },
    [formErrors, incompleteFields]
  );

  // Validation du formulaire
  const validateForm = useCallback(() => {
    const errors = {};

    if (!user.firstName.trim()) errors.firstName = "Le prénom est requis";
    if (!user.lastName.trim()) errors.lastName = "Le nom est requis";
    if (!user.email.trim()) {
      errors.email = "L'email est requis";
    } else if (!/\S+@\S+\.\S+/.test(user.email)) {
      errors.email = "Format d'email invalide";
    }

    if (showPasswordChange) {
      if (!passwords.currentPassword)
        errors.currentPassword = "Mot de passe actuel requis";
      if (!passwords.newPassword) {
        errors.newPassword = "Nouveau mot de passe requis";
      } else if (passwords.newPassword.length < 8) {
        errors.newPassword = "Minimum 8 caractères";
      }
      if (passwords.newPassword !== passwords.confirmPassword) {
        errors.confirmPassword = "Les mots de passe ne correspondent pas";
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  }, [
    user.firstName,
    user.lastName,
    user.email,
    passwords,
    showPasswordChange,
  ]);

  // Soumission du formulaire avec meilleure redirection
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      toast.error("Veuillez corriger les erreurs");
      return;
    }

    setSaving(true);
    try {
      const userData = { ...user };
      if (
        showPasswordChange &&
        passwords.currentPassword &&
        passwords.newPassword
      ) {
        userData.currentPassword = passwords.currentPassword;
        userData.newPassword = passwords.newPassword;
      }

      await updateCurrentUser(userData);
      localStorage.setItem(
        "userName",
        `${userData.firstName} ${userData.lastName}`
      );

      // Vérifier si tous les champs requis sont remplis
      const allFieldsFilled = areAllRequiredFieldsFilled();

      if (pendingCheckout) {
        if (allFieldsFilled) {
          toast.success("Profil mis à jour. Redirection vers le paiement...");
          // Délai plus court pour une meilleure expérience utilisateur
          setTimeout(() => navigate("/checkout"), 800);
        } else {
          toast.success("Profil mis à jour");
          toast.warning("Certains champs requis sont encore manquants");
        }
      } else {
        toast.success("Profil mis à jour avec succès");

        if (showPasswordChange) {
          setPasswords({
            currentPassword: "",
            newPassword: "",
            confirmPassword: "",
          });
          setShowPasswordChange(false);
        }
      }
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error("Mot de passe actuel incorrect");
      } else {
        toast.error(error.response?.data?.message || "Erreur de mise à jour");
      }
    } finally {
      setSaving(false);
    }
  };

  // Vérifier si un champ est incomplet - maintenu mais supporte la suppression immédiate
  const isIncompleteField = (fieldName) => incompleteFields.includes(fieldName);

  // Affichage du chargement
  if (loading) {
    return (
      <div className="container text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Chargement...</span>
        </div>
        <p className="mt-2">Chargement de votre profil...</p>
      </div>
    );
  }

  return (
    <div className="container py-4">
      {/* En-tête */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="mb-0">Mon profil</h2>
        {pendingCheckout ? (
          <div className="alert alert-info d-inline-flex align-items-center m-0 py-2">
            <i className="bi bi-info-circle me-2"></i>
            <span>
              Veuillez compléter votre profil pour finaliser la commande
            </span>
          </div>
        ) : (
          <Link to="/" className="btn btn-outline-primary">
            <i className="bi bi-house-door me-1"></i>
            Accueil
          </Link>
        )}
      </div>

      {/* Formulaire principal */}
      <div className="card shadow-sm">
        <div className="card-body p-4">
          <form onSubmit={handleSubmit}>
            {/* Informations personnelles */}
            <div className="row">
              <div className="col-md-6 mb-3">
                <label htmlFor="firstName" className="form-label fw-bold">
                  Prénom
                  {isIncompleteField("firstName") && (
                    <span className="text-danger ms-1">*</span>
                  )}
                </label>
                <input
                  id="firstName"
                  className={`form-control ${
                    formErrors.firstName ? "is-invalid" : ""
                  } 
                  ${isIncompleteField("firstName") ? "border-danger" : ""}`}
                  name="firstName"
                  value={user.firstName}
                  onChange={handleChange}
                  required
                />
                {formErrors.firstName && (
                  <div className="invalid-feedback">{formErrors.firstName}</div>
                )}
              </div>
              <div className="col-md-6 mb-3">
                <label htmlFor="lastName" className="form-label fw-bold">
                  Nom
                  {isIncompleteField("lastName") && (
                    <span className="text-danger ms-1">*</span>
                  )}
                </label>
                <input
                  id="lastName"
                  className={`form-control ${
                    formErrors.lastName ? "is-invalid" : ""
                  } 
                  ${isIncompleteField("lastName") ? "border-danger" : ""}`}
                  name="lastName"
                  value={user.lastName}
                  onChange={handleChange}
                  required
                />
                {formErrors.lastName && (
                  <div className="invalid-feedback">{formErrors.lastName}</div>
                )}
              </div>
            </div>

            <div className="row">
              <div className="col-md-6 mb-3">
                <label htmlFor="email" className="form-label fw-bold">
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  className={`form-control ${
                    formErrors.email ? "is-invalid" : ""
                  }`}
                  name="email"
                  value={user.email}
                  onChange={handleChange}
                  required
                />
                {formErrors.email && (
                  <div className="invalid-feedback">{formErrors.email}</div>
                )}
              </div>
              <div className="col-md-6 mb-3">
                <label htmlFor="phone" className="form-label fw-bold">
                  Téléphone
                  {isIncompleteField("phone") && (
                    <span className="text-danger ms-1">*</span>
                  )}
                </label>
                <input
                  id="phone"
                  className={`form-control ${
                    isIncompleteField("phone") ? "border-danger" : ""
                  }`}
                  name="phone"
                  value={user.phone || ""}
                  onChange={handleChange}
                  placeholder="+32 123 456 789"
                />
              </div>
            </div>

            <div className="row">
              <div className="col-md-6 mb-3">
                <label htmlFor="gender" className="form-label fw-bold">
                  Genre
                </label>
                <select
                  id="gender"
                  className="form-select"
                  name="gender"
                  value={user.gender || ""}
                  onChange={handleChange}
                >
                  <option value="">Sélectionnez...</option>
                  <option value="homme">Homme</option>
                  <option value="femme">Femme</option>
                  <option value="autre">Autre</option>
                </select>
              </div>
              <div className="col-md-6 mb-3">
                <div className="mt-4">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => setShowPasswordChange(!showPasswordChange)}
                  >
                    <i
                      className={`bi ${
                        showPasswordChange ? "bi-dash-circle" : "bi-key"
                      } me-1`}
                    ></i>
                    {showPasswordChange
                      ? "Annuler le changement"
                      : "Changer mot de passe"}
                  </button>
                </div>
              </div>
            </div>

            {/* Panneau de changement de mot de passe */}
            {showPasswordChange && (
              <div className="card mb-4 bg-light">
                <div className="card-body">
                  <h5 className="mb-3">Changement de mot de passe</h5>
                  <div className="row">
                    <div className="col-md-4 mb-3">
                      <label htmlFor="currentPassword" className="form-label">
                        Mot de passe actuel
                      </label>
                      <input
                        id="currentPassword"
                        type="password"
                        className={`form-control ${
                          formErrors.currentPassword ? "is-invalid" : ""
                        }`}
                        name="password.currentPassword"
                        value={passwords.currentPassword}
                        onChange={handleChange}
                      />
                      {formErrors.currentPassword && (
                        <div className="invalid-feedback">
                          {formErrors.currentPassword}
                        </div>
                      )}
                    </div>
                    <div className="col-md-4 mb-3">
                      <label htmlFor="newPassword" className="form-label">
                        Nouveau mot de passe
                      </label>
                      <input
                        id="newPassword"
                        type="password"
                        className={`form-control ${
                          formErrors.newPassword ? "is-invalid" : ""
                        }`}
                        name="password.newPassword"
                        value={passwords.newPassword}
                        onChange={handleChange}
                      />
                      {formErrors.newPassword && (
                        <div className="invalid-feedback">
                          {formErrors.newPassword}
                        </div>
                      )}
                    </div>
                    <div className="col-md-4 mb-3">
                      <label htmlFor="confirmPassword" className="form-label">
                        Confirmer mot de passe
                      </label>
                      <input
                        id="confirmPassword"
                        type="password"
                        className={`form-control ${
                          formErrors.confirmPassword ? "is-invalid" : ""
                        }`}
                        name="password.confirmPassword"
                        value={passwords.confirmPassword}
                        onChange={handleChange}
                      />
                      {formErrors.confirmPassword && (
                        <div className="invalid-feedback">
                          {formErrors.confirmPassword}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            <hr className="my-4" />

            {/* Adresse */}
            <h5 className="mb-3">
              <i className="bi bi-geo-alt me-2"></i>
              Adresse
              {pendingCheckout && (
                <span className="text-danger small fw-normal ms-2">
                  (Nécessaire pour la commande)
                </span>
              )}
            </h5>

            <div className="row">
              <div className="col-md-8 mb-3">
                <label htmlFor="street" className="form-label">
                  Rue et numéro
                  {isIncompleteField("address.street") && (
                    <span className="text-danger ms-1">*</span>
                  )}
                </label>
                <input
                  id="street"
                  ref={streetInputRef}
                  className={`form-control ${
                    isIncompleteField("address.street") ? "border-danger" : ""
                  }`}
                  placeholder="Commencez à taper une adresse..."
                  name="address.street"
                  value={user.address.street || ""}
                  onChange={handleChange}
                />
                {!placesLoaded && GOOGLE_MAPS_API_KEY && (
                  <small className="text-muted">
                    Chargement de l'autocomplétion...
                  </small>
                )}
                {!GOOGLE_MAPS_API_KEY && (
                  <small className="text-danger">
                    Service d'autocomplétion non disponible
                  </small>
                )}
              </div>
              <div className="col-md-4 mb-3">
                <label htmlFor="address2" className="form-label">
                  Complément
                </label>
                <input
                  id="address2"
                  className="form-control"
                  placeholder="Appartement, bâtiment, etc."
                  name="address.address2"
                  value={user.address.address2 || ""}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="row">
              <div className="col-md-4 mb-3">
                <label htmlFor="postalCode" className="form-label">
                  Code Postal
                  {isIncompleteField("address.postalCode") && (
                    <span className="text-danger ms-1">*</span>
                  )}
                </label>
                <input
                  id="postalCode"
                  className={`form-control ${
                    isIncompleteField("address.postalCode")
                      ? "border-danger"
                      : ""
                  }`}
                  placeholder="1234"
                  name="address.postalCode"
                  value={user.address.postalCode || ""}
                  onChange={handleChange}
                />
              </div>
              <div className="col-md-4 mb-3">
                <label htmlFor="city" className="form-label">
                  Ville
                  {isIncompleteField("address.city") && (
                    <span className="text-danger ms-1">*</span>
                  )}
                </label>
                <input
                  id="city"
                  className={`form-control ${
                    isIncompleteField("address.city") ? "border-danger" : ""
                  }`}
                  placeholder="Ville"
                  name="address.city"
                  value={user.address.city || ""}
                  onChange={handleChange}
                />
              </div>
              <div className="col-md-4 mb-3">
                <label htmlFor="country" className="form-label">
                  Pays
                  {isIncompleteField("address.country") && (
                    <span className="text-danger ms-1">*</span>
                  )}
                </label>
                <input
                  id="country"
                  className={`form-control ${
                    isIncompleteField("address.country") ? "border-danger" : ""
                  }`}
                  placeholder="Pays"
                  name="address.country"
                  value={user.address.country || ""}
                  onChange={handleChange}
                />
              </div>
            </div>

            {/* Boutons d'action */}
            <div className="d-flex justify-content-between mt-4 pt-3 border-top">
              {pendingCheckout ? (
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => navigate("/cart")}
                  disabled={saving}
                >
                  <i className="bi bi-cart me-1"></i>
                  Retour au panier
                </button>
              ) : (
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => navigate("/")}
                  disabled={saving}
                >
                  <i className="bi bi-x-circle me-1"></i>
                  Annuler
                </button>
              )}

              <button
                type="submit"
                className="btn btn-primary"
                disabled={saving}
              >
                {saving ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    Enregistrement...
                  </>
                ) : pendingCheckout ? (
                  <>
                    <i className="bi bi-check-circle me-1"></i>
                    Sauvegarder et continuer
                  </>
                ) : (
                  <>
                    <i className="bi bi-check-circle me-1"></i>
                    Sauvegarder
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default Profile;
